import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { parseFrontMatter } from "../../../shared/parseFrontMatter";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { AccessMapPanel } from "../AccessMapPanel";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { DangerSection } from "../Settings/DangerSection";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { useScopeAccessCloseWarning } from "../ScopeAccessCloseWarning";
import { TabLinkButton } from "../TabButton";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { SettingsTab, type SkillForm } from "./SettingsTab";
import { SkillAccessTab } from "./SkillAccessTab";

type Tab = "settings" | "access";

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

type Props = {
  id: string;
  onClose: () => void;
};

export function SkillModal({ id, onClose }: Props) {
  const z = useZero();
  const [skill] = useQuery(queries.loadSkill({ skillId: id }));
  const canWrite = useHasAccess("skill:write", skill?.id);

  const { tab } = useCurrentUrl(routes.modal.skill, { id });
  const activeTab = tab as Tab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.modal.skill, { id, tab: next });

  const [form, setForm] = useState<SkillForm | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const seededFor = useRef<string | null>(null);
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });

  useEffect(() => {
    if (skill && seededFor.current !== skill.id) {
      seededFor.current = skill.id;
      setForm({
        name: skill.name,
        description: skill.description,
        instructions: skill.instructions,
        enabled: skill.enabled ?? true,
      });
    }
  }, [skill]);

  if (!skill || !form) return null;

  const fresh = skill.fresh;

  const commit = (next: SkillForm) => {
    if (!canWrite) return;
    z.mutate(
      sharedMutators.updateSkill({
        skillId: id,
        name: next.name,
        description: next.description,
        instructions: next.instructions,
        enabled: next.enabled,
      })
    );
  };

  const update = (patch: Partial<SkillForm>) => {
    const next = { ...form, ...patch };
    setForm(next);
    setIsDirty(true);
    if ("enabled" in patch) commit(next);
  };

  const discardDraft = () => {
    z.mutate(sharedMutators.deleteSkill({ skillId: id }));
    onClose();
  };

  const dismiss = () => {
    if (fresh && canWrite) {
      if (isDirty) setConfirmDiscard(true);
      else discardDraft();
      return;
    }
    onClose();
  };

  const saveAndClose = () => {
    commit({ ...form, name: form.name.trim() || "Untitled skill" });
    z.mutate(sharedMutators.publishSkill({ skillId: id }));
    onClose();
  };

  const close = () => requestClose(dismiss);
  const finish = () => requestClose(canWrite && fresh ? saveAndClose : dismiss);

  return (
    <>
      <Modal
        open
        onClose={close}
        style={{
          width: 680,
          height: 760,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header>
          <HeaderLeft>
            <ConnectorLogoContainer
              size={32}
              borderRadius={8}
              hoverBackground={false}
            >
              <Icon name="graduation-cap" size={16} />
            </ConnectorLogoContainer>
            <Text variant="text-regular-md" ellipsis>
              {fresh ? "New skill" : skill.name || "Skill"}
            </Text>
          </HeaderLeft>
          <Tabs>
            <TabLinkButton
              to={makeUrl(routes.modal.skill, { id, tab: "settings" })}
              className={activeTab === "settings" ? "active" : undefined}
            >
              <Icon name="cog" size={16} />
              <Text variant="text-small-md">Settings</Text>
            </TabLinkButton>
            <TabLinkButton
              to={makeUrl(routes.modal.skill, { id, tab: "access" })}
              className={activeTab === "access" ? "active" : undefined}
            >
              <Icon name="lock" size={16} />
              <Text variant="text-small-md">Access</Text>
            </TabLinkButton>
          </Tabs>
          <HeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </HeaderRight>
        </Header>
        <Body>
          {activeTab === "access" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {!fresh && (
                <AccessMapPanel
                  nodeId={skill.id}
                  noun="skill"
                  guard={requestClose}
                />
              )}
              <SkillAccessTab
                skill={skill}
                canManage={canWrite}
                onEdit={() => setIsDirty(true)}
                stagedGrantsRef={stagedGrantsRef}
              />
            </div>
          ) : (
            <>
              <SettingsTab
                form={form}
                disabled={!canWrite}
                onChange={update}
                onCommit={() => commit(form)}
                onUpload={(text) => {
                  const { attrs, body } = parseFrontMatter(text);
                  const next = {
                    ...form,
                    name: attrs.name || form.name,
                    description: attrs.description || form.description,
                    instructions: body.trim(),
                  };
                  setForm(next);
                  setIsDirty(true);
                  commit(next);
                }}
              />
              {canWrite && !fresh && (
                <DangerSection
                  style={{ marginTop: 16 }}
                  title="Delete skill"
                  description="Permanently deletes this skill and its access settings."
                  actionLabel="Delete skill"
                  confirm={{
                    confirmLabel: "Delete",
                    onConfirm: () => {
                      z.mutate(sharedMutators.deleteSkill({ skillId: id }));
                      onClose();
                    },
                  }}
                />
              )}
            </>
          )}
        </Body>
        <Footer>
          <div />
          <FooterRight>
            <Text variant="text-small-md" color="var(--text-primary)">
              Available to AI
            </Text>
            <Toggle
              isOn={form.enabled}
              disabled={!canWrite}
              onToggle={() => update({ enabled: !form.enabled })}
            />
            {canWrite && fresh && activeTab !== "access" ? (
              <Button
                variant="primary"
                size="l"
                onClick={() => setActiveTab("access")}
              >
                Next
              </Button>
            ) : (
              <Button variant="primary" size="l" onClick={finish}>
                {canWrite && fresh ? "Save" : "Done"}
              </Button>
            )}
          </FooterRight>
        </Footer>
      </Modal>
      {closeWarningModal}
      <ConfirmModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={discardDraft}
        title="Discard new skill?"
        description="Your changes will be lost if you continue."
        confirmLabel="Discard"
        destructive
      />
    </>
  );
}
